/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, {useCallback, useEffect, useState, useMemo} from 'react';
// redux
import {useSelector, useDispatch} from 'react-redux';

// material
import {Switch, CheckboxField, SingleSelectDropDown } from '@insticator/insticator-ui';
import SectionCard from '../../../../../material/expandablesection/sectioncard/sectioncard';
import SectionGroup from '../../../../../material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription
    from '../../../../../material/expandablesection/sectiongroupdescription/sectiongroupdescription';
// styles
import injectSheet from 'react-jss';
import styles from './profile_styles';
import ConfigController from "../../../../../material/expandablesection/controllers/configcontroller/configcontroller";
import ExpandableSection from "../../../../../material/expandablesection/expandablesection";
import PreviewConfig from 'pages/dashboard/commenting/commentingconfigv2/previewconfig/previewconfig';

import { ReactComponent as CaretDown } from 'assets/icons/caret-down.svg'
import { fetchProfileConfig, updateProfileConfig } from 'state/thunks/profile';
import { sortArrays } from 'utils/sortArrays';
import { GENDER_OPTIONS } from 'utils/consts';
import AboutSection from './aboutsection/aboutsection';

const genderOptionsListMap = {
    MALE: 'Male',
    FEMALE: 'Female',
    NON_BINARY: 'Non-binary',
    NOT_SPECIFIED: 'Not specified'
}

/* ========== ~~~~~~~~~~ ADVANCED SETTINGS - SENTIMENT ~~~~~~~~~~ ========== */
const ProfileConfig = injectSheet(styles)((props) => {
    const dispatch = useDispatch();
    // app state
    const { sites } = useSelector((state) => state.account);
    const { siteUUID: initialSiteUUID } = useSelector((state) => state.session);
    const { profileConfig: savedProfileConfig } = useSelector(state => state.profile)

    // local state
    const [isOpened, setIsOpened] = useState(false);
    const [siteUUID, setSiteUUID] = useState(initialSiteUUID);

    const [profileConfig, setProfileConfig] = useState(savedProfileConfig)
    const [isProcessing, setIsProcessing] = useState(false)

    const [isScollIntoView, setIsScrollIntoView] = useState(false)

    let currentLocation = window.location.href;
    const hasUrlAnchor = currentLocation.includes("?view=genderConfig") || currentLocation.includes('?view=aboutConfig');

    useEffect(() => {
      if(hasUrlAnchor) setIsOpened(true)
    },[hasUrlAnchor])
  
    const scrollToConfig = useCallback(() => {
        if (hasUrlAnchor) {
          const anchorElement = document.getElementById(currentLocation.includes('?view=genderConfig') ? 'gender-config' : 'about-config');
          if(anchorElement){
              setTimeout(() => {
                anchorElement.scrollIntoView({ behavior: "smooth",  block: 'center'  })
                setIsScrollIntoView(true)
              }, 700)
          }
        }
    },[currentLocation, hasUrlAnchor])
    
    useEffect(() => {
      if(isOpened && !isScollIntoView) scrollToConfig()
    }, [isOpened, isScollIntoView, scrollToConfig])
  

    useEffect(() => {
        if (siteUUID) dispatch(fetchProfileConfig(siteUUID));
    }, [dispatch, siteUUID]);

    useEffect(() => {
        console.log('saved', savedProfileConfig)
        setProfileConfig(savedProfileConfig)
    }, [savedProfileConfig])

    const isChanged = useMemo(() => {
        const isFieldsChanged = !(JSON.stringify(profileConfig) === JSON.stringify(savedProfileConfig));
        return isFieldsChanged
      }, [profileConfig, savedProfileConfig])

    const handleToggle = useCallback(() => {
        setIsOpened(!isOpened);
    }, [isOpened]);

    const handleCancel = () => {
        setIsOpened(false)
        setProfileConfig(savedProfileConfig)
    }

    const handleSave = async () => {
        setIsProcessing(true)
        const response = await dispatch(updateProfileConfig({ config: profileConfig, configUUID: profileConfig.uuid }))
        if(response.payload) setIsOpened(false)
        setIsProcessing(false)
    }

    const isGenderOptionEnabled = (gender) => {
        return profileConfig.genderOptions.includes(gender)
    }  
      
    const handleUseGenderChange = () => setProfileConfig({...profileConfig, useGender: !profileConfig.useGender})

    const handleGenderOptionChange = useCallback((property, value) => {
        const currentGenderOptions = [...profileConfig.genderOptions]
        let newGenderList = [...currentGenderOptions]
        if(!value) {
            newGenderList = currentGenderOptions.filter(gender => gender !== property)
        } else {
            newGenderList.push(property)
        }
        const sortedGenderOptions = sortArrays([...GENDER_OPTIONS], [...newGenderList])
        setProfileConfig({...profileConfig, genderOptions: sortedGenderOptions, useGender: !!sortedGenderOptions.length})
    },[profileConfig])

    const genderPreviewList = useMemo(() => {
        return profileConfig.genderOptions.length ? (
          <div className='genderpreview'>
            {profileConfig.genderOptions.map((opt, index) => {
                return (
                    <span className={index === 0 ? 'selected' : ''}>
                        {index === 0 && <CaretDown className='caret' fill='#FFF' />}
                        {genderOptionsListMap[opt]}
                    </span>
                )
            })}
          </div>
        ) : ''
      }, [profileConfig])
    
    const handleProfileConfigChange = (config) => {
        setProfileConfig({...profileConfig, ...config})
    }

    const controller = (
        <ConfigController
            handleToggle={handleToggle}
            handleSave={handleSave}
            handleCancel={handleToggle}
            isOpened={isOpened}
            isChanged={isChanged}
            isProcessing={isProcessing}
            title='Profile'
            description={''}
            cancelTitle={'Close'}
        />
    );

    return (
        <ExpandableSection
            className={`profileconfig ${props.classes.profileconfig}`}
            controller={controller}
            isOpened={isOpened}
            handleCancel={handleCancel}
            cancelTitle={'Cancel'}
        >
            <SectionCard>
                <SectionGroup column>
                    <SingleSelectDropDown 
                        name={sites[siteUUID]} 
                        searchable={false} 
                        onSelect={selected => setSiteUUID(selected.value)} 
                        items={Object.keys(sites).map(key => ({ name: sites[key], value: key, dir: 'ASC' }))}
                    />
                    <AboutSection onChange={handleProfileConfigChange} useAbout={profileConfig.useAbout} aboutSectionOptions={profileConfig.aboutSectionOptions} />
                    {/* TODO: Move separate for gender config */}
                    <div id="gender-config">
                    <SectionGroup row>
                        <SectionGroup column>
                            <SectionGroupDescription
                                title={'Gender'}
                                description={''}
                            />
                        <SectionGroup row>
                            <Switch variant={'primary'} handleClick={handleUseGenderChange}
                                    on={profileConfig.useGender}/>
                            <SectionGroupDescription
                                title={'Allow users to specify their gender'}
                                description={''}
                            />
                        </SectionGroup>
                        <SectionGroup child column>
                            <CheckboxField type={`checkbox`}
                                            name={`gender`}
                                            valid={true}
                                            disabled={!profileConfig.useGender}
                                            checked={isGenderOptionEnabled('MALE')}
                                            handleChange={() => handleGenderOptionChange('MALE', !isGenderOptionEnabled('MALE'))}
                                            label={'Male'}/>
                            <CheckboxField type={`checkbox`}
                                            name={`gender`}
                                            valid={true}
                                            disabled={!profileConfig.useGender}
                                            checked={isGenderOptionEnabled('FEMALE')}
                                            handleChange={() => handleGenderOptionChange('FEMALE', !isGenderOptionEnabled('FEMALE'))}
                                            label={'Female'}/>
                            <CheckboxField type={`checkbox`}
                                            name={`gender`}
                                            valid={true}
                                            disabled={!profileConfig.useGender}
                                            checked={isGenderOptionEnabled('NON_BINARY')}
                                            handleChange={() => handleGenderOptionChange('NON_BINARY', !isGenderOptionEnabled('NON_BINARY'))}
                                            label={'Non-binary'}/>
                            <CheckboxField type={`checkbox`}
                                            name={`gender`}
                                            valid={true}
                                            disabled={!profileConfig.useGender}
                                            checked={isGenderOptionEnabled('NOT_SPECIFIED')}
                                            handleChange={() => handleGenderOptionChange('NOT_SPECIFIED', !isGenderOptionEnabled('NOT_SPECIFIED'))}
                                            label={'Not specified'}/>
                        </SectionGroup>
                        </SectionGroup>
                        <PreviewConfig>
                            {genderPreviewList} 
                        </PreviewConfig>
                    </SectionGroup>
                    </div>
                </SectionGroup>
            </SectionCard>
        </ExpandableSection>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default ProfileConfig;

